import React, { useEffect, useState, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { NAV } from "@/constants/testIds";
import AudreyFAB from "@/components/AudreyFAB";
import MobileBottomTabs, { tabsHiddenOn } from "@/components/MobileBottomTabs";
import { PlanTag, LockIcon, LockedRow, hasPlanAccess } from "@/components/PlanTag";
import { Coins, Infinity as InfinityIcon } from "lucide-react";
import { LogOut, ShieldCheck, Smartphone, Plus, MessageSquare, Grape, MapPin, NotebookPen,
  Code2, Menu, X, LogIn, Building2, CalendarDays, Sparkles, PanelLeftClose, PanelLeftOpen,
  CreditCard, Camera, Gamepad2, Brain, GraduationCap, User, Compass, Route as RouteIcon, Swords, Megaphone, LayoutGrid,
  Heart, Zap, BookOpen, Mic, KeyRound, Trophy, ShoppingBag, Globe2, CalendarClock, Ticket, ScanLine,
  Wine,
} from "lucide-react";
import { useCart } from "@/lib/cart";
import { useSRSCount } from "@/lib/srsCount";

const Logo = ({ collapsed = false, compact = false }) =>
  collapsed ? (
    <img src="/brand/mark.png" alt="geoVINUM" className="w-9 h-9 rounded-lg" />
  ) : (
    <img src="/brand/logo-color.png" alt="geoVINUM" className={compact ? "h-7 w-auto" : "h-9 w-auto"} />
  );

const LangSwitcher = ({ compact = false }) => {
  const { lang, setLang } = useT();
  return (
    <div className="inline-flex items-center gap-0.5 rounded-full border border-[#1F3A5F]/15 overflow-hidden bg-white">
      {["fr", "en", "es", "pt"].map((l) => (
        <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
          className={`px-2.5 py-1 text-[11px] font-bold tracking-wider uppercase transition-colors ${
            lang === l ? "bg-[#1F3A5F] text-white" : "text-[#8B95A1] hover:text-[#1F3A5F]"
          }`}>{l}</button>
      ))}
    </div>
  );
};

const InstallButton = () => {
  const [evt, setEvt] = useState(null);
  const [hidden, setHidden] = useState(false);
  const { t } = useT();
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setEvt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => setHidden(true));
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);
  if (!evt || hidden) return null;
  return (
    <button data-testid="install-pwa" onClick={async () => { await evt.prompt(); setHidden(true); }}
      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#1F3A5F] text-white text-xs font-bold hover:bg-[#2A4870] transition-colors">
      <Smartphone className="w-3.5 h-3.5" /> {t("nav.install")}
    </button>
  );
};

/** ━━ Experience nav block ━━ Regroups all Experience entrances:
 * - "Créer un événement" for anyone logged in (organizer entry)
 * - "Mon billet" if a participant session token exists, else "J'ai un code"
 * - "Espace serveur" scanner
 */
const ExperienceSection = ({ user, lang, closeIfMobile, linkClass }) => {
  const [hasParticipant, setHasParticipant] = useState(false);
  const isProUser = !!(user && (user.plan === "pro" || user.role === "admin"));
  useEffect(() => {
    const check = () => setHasParticipant(!!localStorage.getItem("geovinum_participant_token"));
    check();
    window.addEventListener("storage", check);
    return () => window.removeEventListener("storage", check);
  }, []);
  return (
    <div className="mt-6">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8B95A1] font-bold px-3 mb-1.5 flex items-center gap-1.5">
        <span>🎟 Experience</span>
        <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-[#E5276A]/10 text-[#E5276A] font-bold tracking-wider">NEW</span>
      </div>
      <div className="space-y-0.5">
        {!!user && (
          <LockedRow locked={!isProUser}>
            <NavLink to={isProUser ? "/pro/experience" : "/pricing"} data-testid="nav-link-pro-experience" onClick={closeIfMobile} className={linkClass}>
              <CalendarClock className="w-4 h-4" /> {lang === "fr" ? "Créer un événement" : "Create event"}
              <span className="ml-auto"><PlanTag plan="pro" /></span>
            </NavLink>
          </LockedRow>
        )}
        {hasParticipant ? (
          <NavLink to="/experience/my" data-testid="nav-link-experience-my" onClick={closeIfMobile} className={linkClass}>
            <Ticket className="w-4 h-4" /> {lang === "fr" ? "Mon billet" : "My ticket"}
          </NavLink>
        ) : (
          <NavLink to="/experience" data-testid="nav-link-experience" onClick={closeIfMobile} className={linkClass}>
            <KeyRound className="w-4 h-4" /> {lang === "fr" ? "J'ai un code" : "I have a code"}
          </NavLink>
        )}
        <NavLink to="/experience/staff" data-testid="nav-link-experience-staff" onClick={closeIfMobile} className={linkClass}>
          <ScanLine className="w-4 h-4" /> {lang === "fr" ? "Espace serveur" : "Staff scanner"}
        </NavLink>
      </div>
    </div>
  );
};

const SidebarContent = ({ onNavigate, collapsed, onToggleCollapse }) => {
  const { t, lang } = useT();
  const { user, logout } = useAuth();
  const { totals: cartTotals } = useCart();
  const { dueToday: srsDueToday, overdue: srsOverdue } = useSRSCount();
  const cartCount = cartTotals?.count || 0;
  const loc = useLocation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  // Sub-menu is exposed under "Espace Pro" for Pro subscribers AND admins
  // (admins see the same experience as a Pro subscriber, cf. permissions.js).
  const isProUser = !!(user && (user.plan === "pro" || user.role === "admin"));
  // Amateur perks (Coach, Suggestions, Explain) are exposed to Amateur/Pro subscribers and admins.
  const isAmateurPlus = !!(user && (user.subscription_active || user.role === "admin"));

  const loadSessions = useCallback(() => {
    if (!user) { setSessions([]); return; }
    api.get("/chat/sessions").then((r) => setSessions(r.data)).catch(() => setSessions([]));
  }, [user]);

  // Reload chat sessions only when the URL relates to /chat (saves N reloads on every nav)
  useEffect(() => {
    if (loc.pathname.startsWith("/chat") || sessions.length === 0) {
      loadSessions();
    }
  }, [loadSessions, loc.pathname, loc.search, sessions.length]);

  const closeIfMobile = () => onNavigate?.();

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
      isActive ? "bg-[#FCE4ED] text-[#E5276A] font-bold" : "text-[#1F3A5F] hover:bg-[#F7F2EE]"
    }`;

  if (collapsed) {
    return (
      <div className="flex flex-col h-full bg-[#FBF7F3] border-r border-[#1F3A5F]/8 items-center py-4 gap-2">
        <Link to="/" onClick={closeIfMobile}><Logo collapsed /></Link>
        <button onClick={onToggleCollapse} data-testid="sidebar-expand" title={t("side.expand")}
          className="mt-2 p-2 rounded-lg text-[#8B95A1] hover:text-[#1F3A5F] hover:bg-white"><PanelLeftOpen className="w-5 h-5" /></button>
        <Link to="/" onClick={closeIfMobile} title={t("side.newChat")}
          className="p-2 rounded-lg text-[#1F3A5F] hover:bg-white"><Plus className="w-5 h-5" /></Link>
        <Link to="/appellations" onClick={closeIfMobile} title="Appellations" className="p-2 rounded-lg text-[#1F3A5F] hover:bg-white"><MapPin className="w-5 h-5" /></Link>
        <Link to="/cepages" onClick={closeIfMobile} title="Grapes" className="p-2 rounded-lg text-[#1F3A5F] hover:bg-white"><Grape className="w-5 h-5" /></Link>
        <Link to="/producers" onClick={closeIfMobile} title="Producers" className="p-2 rounded-lg text-[#1F3A5F] hover:bg-white"><Building2 className="w-5 h-5" /></Link>
        <Link to="/events" onClick={closeIfMobile} title="Events" className="p-2 rounded-lg text-[#1F3A5F] hover:bg-white"><CalendarDays className="w-5 h-5" /></Link>
        <Link to="/shop" onClick={closeIfMobile} title="Shop" className="p-2 rounded-lg text-[#1F3A5F] hover:bg-white"><ShoppingBag className="w-5 h-5" /></Link>
        <div className="flex-1" />
        {user ? (
          <div className="w-9 h-9 rounded-full bg-[#1F3A5F] text-white flex items-center justify-center text-xs font-bold" title={user.name}>
            {user.name?.[0]?.toUpperCase()}
          </div>
        ) : (
          <Link to="/login" className="p-2 rounded-lg text-[#1F3A5F] hover:bg-white" title="Sign in"><LogIn className="w-5 h-5" /></Link>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[#FBF7F3] border-r border-[#1F3A5F]/8">
      <div className="px-4 pr-12 md:pr-4 pt-4 pb-3 flex items-start justify-between">
        <Link to="/" onClick={closeIfMobile} data-testid={NAV.logo} className="block">
          <Logo />
          <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#FCE4ED] text-[#E5276A] text-[10px] font-bold tracking-[0.18em] uppercase">
            <span>{lang === "en" ? "geoVINUM AI" : "geoVINUM AI"}</span>
          </div>
        </Link>
        <div className="flex flex-col items-end gap-2">
          <button onClick={onToggleCollapse} data-testid="sidebar-collapse" title={t("side.collapse")}
            className="hidden md:inline-flex p-1.5 rounded-md text-[#8B95A1] hover:text-[#1F3A5F] hover:bg-white">
            <PanelLeftClose className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="px-3 pb-2">
        <Link to="/" onClick={closeIfMobile} data-testid="side-new-chat"
          className="w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-[#1F3A5F]/12 hover:border-[#E5276A] hover:bg-[#FFF8FB] text-sm font-bold text-[#1F3A5F] transition-colors">
          <Plus className="w-4 h-4" /> {t("side.newChat")}
        </Link>
      </div>

      <div className="px-3 mt-2 flex-1 overflow-y-auto">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8B95A1] font-bold px-3 mb-1.5">{t("side.recent")}</div>
        {!user && <div className="px-3 py-2 text-xs text-[#8B95A1] italic">{t("side.signInToSave")}</div>}
        {user && sessions.length === 0 && <div className="px-3 py-2 text-xs text-[#8B95A1] italic">{t("side.empty")}</div>}
        <div className="space-y-0.5">
          {sessions.map((s) => (
            <button key={s.session_id}
              onClick={() => { navigate(`/chat?s=${s.session_id}`); closeIfMobile(); }}
              data-testid={`side-session-${s.session_id}`}
              className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                loc.search.includes(s.session_id) ? "bg-[#FCE4ED] text-[#E5276A]" : "text-[#1F3A5F] hover:bg-[#F7F2EE]"
              }`}>
              <MessageSquare className="w-3.5 h-3.5 shrink-0 opacity-70" />
              <span className="truncate text-[13px]">{s.title}</span>
            </button>
          ))}
        </div>

        {/* ━━━━━ ESSENTIELS — 4 piliers conversationnels ━━━━━ */}
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8B95A1] font-bold px-3 mb-1.5">
            {t("sb.essentials")}
          </div>
          <div className="space-y-0.5">
            <NavLink to="/" data-testid="nav-link-home-ai" onClick={closeIfMobile} end className={linkClass}>
              <Sparkles className="w-4 h-4" /> {t("brand.product")}
            </NavLink>
            <NavLink to="/hub/experience" data-testid="nav-link-hub-experience" onClick={closeIfMobile} className={linkClass}>
              <Wine className="w-4 h-4" /> {t("hub.experience.label")}
            </NavLink>
            <NavLink to="/hub/learn" data-testid="nav-link-hub-learn" onClick={closeIfMobile} className={linkClass}>
              <BookOpen className="w-4 h-4" /> {t("hub.learn.label")}
            </NavLink>
            <NavLink to="/hub/taste" data-testid="nav-link-hub-taste" onClick={closeIfMobile} className={linkClass}>
              <Brain className="w-4 h-4" /> {t("hub.taste.label")}
            </NavLink>
            <NavLink to="/hub/play" data-testid="nav-link-hub-play" onClick={closeIfMobile} className={linkClass}>
              <Gamepad2 className="w-4 h-4" /> {t("hub.play.label")}
            </NavLink>
          </div>
        </div>

        {/* ━━━━━ PRO — chat Pro IA + sous-menu classique ━━━━━ */}
        <ProSection
          isProUser={isProUser}
          lang={lang}
          closeIfMobile={closeIfMobile}
          linkClass={linkClass}
          isLogged={!!user}
        />

        {/* ━━━━━ ALL TABS (ancien FullMenu, en accordéon) ━━━━━ */}
        <FullMenu
          user={user} lang={lang} closeIfMobile={closeIfMobile} linkClass={linkClass}
          isProUser={isProUser} isAmateurPlus={isAmateurPlus}
          srsDueToday={srsDueToday} srsOverdue={srsOverdue}
          cartCount={cartCount}
        />

        {/* "Toutes les fonctionnalités" removed — sub-features are now
           suggested conversationally inside each Hub page (/hub/:pillar). */}
        {/* note: FullMenu is re-added below the Pro section, kept collapsed by default. */}

        {/* ━━━━━ ACCOUNT ━━━━━ */}
        <div className="mt-6 mb-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8B95A1] font-bold px-3 mb-1.5">{t("sb.account")}</div>
          <div className="space-y-0.5">
            <NavLink to="/pricing" data-testid="nav-link-pricing" onClick={closeIfMobile} className={linkClass}>
              <CreditCard className="w-4 h-4" /> {t("sb.pricing")}
            </NavLink>
            <NavLink to="/guide" data-testid="nav-link-guide" onClick={closeIfMobile} className={linkClass}>
              <BookOpen className="w-4 h-4" /> {t("sb.userguide")}
            </NavLink>
            {user && (
              <NavLink to="/account" data-testid="nav-link-account" onClick={closeIfMobile} className={linkClass}>
                <User className="w-4 h-4" /> {t("sb.myAccount")}
              </NavLink>
            )}
            {user?.role === "admin" && (
              <a
                href="https://geovinum.io/admin"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={NAV.admin}
                onClick={closeIfMobile}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[#4A5568] hover:bg-[#FBF7F3] hover:text-[#E5276A]"
              >
                <ShieldCheck className="w-4 h-4" /> {t("nav.admin")}
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="px-3 py-3 border-t border-[#1F3A5F]/8 space-y-2">
        <InstallButton />
        <div className="flex justify-center">
          <LangSwitcher />
        </div>
        <div className="flex items-center justify-end gap-2">
          {user ? (
            <div className="flex items-center gap-1.5 w-full">
              <Link to="/pricing" data-testid="credits-badge"
                    title={isAmateurPlus ? "Crédits IA illimités" : `${user.credits ?? 0} crédits IA restants — cliquez pour recharger`}
                    className={`flex-1 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold transition ${
                      isAmateurPlus
                        ? "bg-gradient-to-r from-[#FCE4ED] to-[#FBF7F3] text-[#E5276A] hover:from-[#FCE4ED] hover:to-[#FCE4ED]"
                        : (user.credits ?? 0) < 3
                          ? "bg-red-50 text-red-600 hover:bg-red-100"
                          : "bg-[#FBF7F3] text-[#1F3A5F] hover:bg-white"
                    }`}>
                {isAmateurPlus ? (
                  <><InfinityIcon className="w-3.5 h-3.5" /> Crédits ∞</>
                ) : (
                  <><Coins className="w-3.5 h-3.5" /> {user.credits ?? 0} crédits</>
                )}
              </Link>
              <div className="w-7 h-7 rounded-full bg-[#1F3A5F] text-white flex items-center justify-center text-xs font-bold" data-testid={NAV.user} title={user.name}>
                {user.name?.[0]?.toUpperCase()}
              </div>
              <button data-testid={NAV.logout} onClick={() => { logout(); closeIfMobile(); navigate("/"); }}
                className="p-1.5 rounded-md text-[#8B95A1] hover:text-[#E5276A] hover:bg-white" title="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 w-full">
              <Link to="/login" data-testid={NAV.login} onClick={closeIfMobile}
                className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold text-[#1F3A5F] hover:bg-white">
                <LogIn className="w-3.5 h-3.5" /> {t("nav.login")}
              </Link>
              <Link to="/register" data-testid={NAV.register} onClick={closeIfMobile}
                className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold text-white bg-[#E5276A] hover:bg-[#C01661]">
                {t("nav.register")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("geovinum_side_collapsed") === "1");
  useEffect(() => { localStorage.setItem("geovinum_side_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-white overflow-x-clip" data-tabs={tabsHiddenOn(pathname) ? "off" : "on"}
         style={{ "--sidebar-w": collapsed ? "4rem" : "18rem" }}>
      {/* Mobile top bar — compact (no lang switcher, moved to sidebar) */}
      <div className="md:hidden sticky top-0 z-30 bg-white border-b border-[#1F3A5F]/8" style={{ paddingTop: "var(--safe-top)" }} data-testid="mobile-top-bar">
        <div className="flex items-center justify-between px-4 h-12">
          <button onClick={() => setOpen(true)} data-testid="open-sidebar" className="p-2 -ml-2 text-[#1F3A5F]" aria-label="menu">
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/" className="flex items-center"><Logo compact /></Link>
          <div className="w-9" aria-hidden="true" />
        </div>
      </div>

      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-[#1F3A5F]/40" onClick={() => setOpen(false)} />
          <div className="relative w-80 max-w-[85vw] h-full bg-[#FBF7F3] animate-fade-up" data-testid="mobile-drawer"
               style={{ paddingTop: "var(--safe-top)", paddingBottom: "var(--safe-bottom)" }}>
            <button onClick={() => setOpen(false)} data-testid="close-sidebar" style={{ top: "calc(var(--safe-top) + 0.75rem)" }}
                    className="absolute right-3 z-10 p-1.5 rounded-lg text-[#1F3A5F] hover:bg-white" aria-label="close">
              <X className="w-5 h-5" />
            </button>
            <SidebarContent onNavigate={() => setOpen(false)} collapsed={false} onToggleCollapse={() => {}} />
          </div>
        </div>
      )}

      <div className="flex">
        <aside className={`hidden md:flex md:flex-col h-screen sticky top-0 shrink-0 transition-[width] duration-200 ${collapsed ? "w-16" : "w-72"}`}>
          <SidebarContent collapsed={collapsed} onToggleCollapse={() => setCollapsed((v) => !v)} />
        </aside>
        <main className="flex-1 min-w-0 overflow-x-clip pb-[var(--m-tabs-h)] md:pb-0">{children}</main>
      </div>
      <MobileBottomTabs />
      <AudreyFAB />
    </div>
  );
}

/* ──────────── FullMenu (menu complet replié par défaut) ──────────── */
function FullMenu({ user, lang, closeIfMobile, linkClass, isProUser, isAmateurPlus, srsDueToday, srsOverdue, cartCount }) {
  const [open, setOpen] = useState(false);
  const { t } = useT();
  return (
    <div className="mt-6">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="sidebar-fullmenu-toggle"
        className="w-full flex items-center justify-between px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-[#8B95A1] font-bold hover:text-[#1F3A5F]"
      >
        <span>{t("sb.allFeatures")}</span>
        <span className={`transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && (
        <div className="space-y-0.5 mt-1" data-testid="sidebar-fullmenu">
          <NavLink to="/learn" onClick={closeIfMobile} className={linkClass}>
            <GraduationCap className="w-4 h-4" /> {t("sb.revise")}
          </NavLink>
          <NavLink to="/learn/coach" onClick={closeIfMobile} className={linkClass}>
            <Sparkles className="w-4 h-4" /> {t("sb.aiCoach")}
          </NavLink>
          <NavLink to="/learn/explain" onClick={closeIfMobile} className={linkClass}>
            <Camera className="w-4 h-4" /> {t("sb.explainPhoto")}
          </NavLink>
          <NavLink to="/revisions" onClick={closeIfMobile} className={linkClass}>
            <CalendarClock className="w-4 h-4" /> Study Schedule
            {(srsDueToday + srsOverdue) > 0 && (
              <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold text-white ${
                srsOverdue > 0 ? "bg-red-600" : "bg-[#E5276A]"
              }`}>{srsDueToday + srsOverdue > 99 ? "99+" : srsDueToday + srsOverdue}</span>
            )}
          </NavLink>
          <NavLink to="/microlearning" onClick={closeIfMobile} className={linkClass}>
            <Zap className="w-4 h-4" /> Microlearning
          </NavLink>
          <NavLink to="/explorer" onClick={closeIfMobile} className={linkClass}>
            <Camera className="w-4 h-4" /> Vision
          </NavLink>
          <NavLink to="/taste/suggestions" onClick={closeIfMobile} className={linkClass}>
            <Heart className="w-4 h-4" /> {t("sb.suggestions")}
          </NavLink>
          <NavLink to="/taste/profile" onClick={closeIfMobile} className={linkClass}>
            <User className="w-4 h-4" /> {t("sb.tasteProfile")}
          </NavLink>
          <NavLink to="/play" onClick={closeIfMobile} className={linkClass} end>
            <Gamepad2 className="w-4 h-4" /> {t("sb.play")}
          </NavLink>
          <NavLink to="/audrey" onClick={closeIfMobile} className={linkClass}>
            <Brain className="w-4 h-4" /> {t("sb.guessAppellation")}
          </NavLink>
          <NavLink to="/deduce" onClick={closeIfMobile} className={linkClass}>
            <Sparkles className="w-4 h-4" /> {t("sb.deduceWine")}
          </NavLink>
          <NavLink to="/appellations" onClick={closeIfMobile} className={linkClass}>
            <MapPin className="w-4 h-4" /> {t("hub.learn.appellations")}
          </NavLink>
          <NavLink to="/cepages" onClick={closeIfMobile} className={linkClass}>
            <Grape className="w-4 h-4" /> {t("sb.grapes")}
          </NavLink>
          <NavLink to="/producers" onClick={closeIfMobile} className={linkClass}>
            <Building2 className="w-4 h-4" /> {t("sb.producers")}
          </NavLink>
          <NavLink to="/events" onClick={closeIfMobile} className={linkClass}>
            <CalendarDays className="w-4 h-4" /> {t("sb.events")}
          </NavLink>
          <NavLink to="/shop" onClick={closeIfMobile} className={linkClass}>
            <ShoppingBag className="w-4 h-4" /> {t("sb.shop")}
            {cartCount > 0 && (
              <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#E5276A] text-white text-[10px] font-bold">{cartCount}</span>
            )}
          </NavLink>
          <NavLink to="/dashboard" onClick={closeIfMobile} className={linkClass}>
            <LayoutGrid className="w-4 h-4" /> Dashboard
          </NavLink>
          <NavLink to="/experience" onClick={closeIfMobile} className={linkClass}>
            <Wine className="w-4 h-4" /> Experience
          </NavLink>
          {isProUser && (
            <NavLink to="/pro/dashboard" onClick={closeIfMobile} className={linkClass}>
              <Code2 className="w-4 h-4" /> Espace Pro
            </NavLink>
          )}
        </div>
      )}
    </div>
  );
}



/* ──────────── ProSection — hub chat Pro + sous-menu classique repliable ──────────── */
function ProSection({ isProUser, lang, closeIfMobile, linkClass, isLogged }) {
  const [open, setOpen] = useState(false);
  const { t } = useT();
  return (
    <div className="mt-6">
      <div className="flex items-center gap-1.5 px-3 mb-1.5">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#8B95A1] font-bold">Pro</span>
        {!isProUser && (
          <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-[#E5276A]/10 text-[#E5276A] font-bold tracking-wider">
            {t("sb.pro.discover")}
          </span>
        )}
      </div>
      <div className="space-y-0.5">
        <NavLink to="/hub/pro" data-testid="nav-link-hub-pro" onClick={closeIfMobile} end className={linkClass}>
          <ShieldCheck className="w-4 h-4" /> {t("sb.pro.ai")}
        </NavLink>
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="sidebar-pro-toggle"
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-[#1F3A5F] hover:bg-[#F7F2EE] transition-colors"
        >
          <span className="inline-flex items-center gap-2.5">
            <LayoutGrid className="w-4 h-4" />
            {t("sb.pro.features")}
          </span>
          <span className={`transition-transform text-xs ${open ? "rotate-180" : ""}`}>▾</span>
        </button>
        {open && (
          <div className="pl-2 space-y-0.5" data-testid="sidebar-pro-submenu">
            {(isLogged && isProUser) ? (
              <>
                <NavLink to="/pro/experience"
                         data-testid="nav-link-pro-experience-sub"
                         onClick={closeIfMobile} end className={linkClass}>
                  <CalendarClock className="w-4 h-4" /> {t("sb.pro.experience")}
                </NavLink>
                <NavLink to="/pro/dashboard"
                         data-testid="nav-link-pro-dashboard-sub"
                         onClick={closeIfMobile} end className={linkClass}>
                  <LayoutGrid className="w-4 h-4" /> {t("sb.pro.dashboard")}
                </NavLink>
                <NavLink to="/pro/sponsored"
                         data-testid="nav-link-pro-sponsored-sub"
                         onClick={closeIfMobile} end className={linkClass}>
                  <Megaphone className="w-4 h-4" /> {t("sb.pro.sponsored")}
                </NavLink>
              </>
            ) : (
              <NavLink to="/pricing"
                       data-testid="nav-link-pro-upgrade-sub"
                       onClick={closeIfMobile} end className={linkClass}>
                <ShieldCheck className="w-4 h-4" /> {t("sb.pro.upgrade")}
              </NavLink>
            )}
            <NavLink to="/pro"
                     data-testid="nav-link-pro-showcase-sub"
                     onClick={closeIfMobile} end className={linkClass}>
              <Code2 className="w-4 h-4" /> {t("sb.pro.showcase")}
            </NavLink>
          </div>
        )}
      </div>
    </div>
  );
}
